import {useRouter} from "next/router";
import {useContext, useEffect, useRef, useState} from "react";
import {getCreatorRoute, SITE_URL} from "common/const";
import {getYoutubeIdFromUrl, useOutsideAlerter} from "common/utils";
import * as Sentry from "@sentry/nextjs";
import UserContext from "context/AuthContext";
import styles from "components/cards/palette/postCard.module.scss";
import classNames from "classnames";
import Image from "next/image";
import DefaultProfileIcon from "images/icons/default_profile.svg";
import utilStyles from "styles/utils.module.scss";
import Nickname from "components/Nickname";
import CreatorBadgeIcon from "images/icons/creator_badge.svg";
import ShowMoreIcon from "images/icons/show_more.svg";
import PostScrapButton from "components/buttons/PostScrapButton";
import ScrapIconActive from "images/icons/heart_fill.svg";
import InactiveScrapIcon from "images/icons/heart_line.svg";
import ShareButton from "components/ShareButton";
import ShareIcon from "images/icons/share.svg";
import {OptionSwiper} from "components/swiper/OptionSwiper";
import {EditPostModal} from "components/modal/EditPostModal";
import {PostContent} from "components/sections/palette/PostContent";
import {ContentSection} from "components/sections/palette/ContentSection";
import SwiperCore, {Navigation, Pagination} from "swiper";
import ModalContext from "context/ModalContext";

let optionSwiperIndex = {};
let expandInfo = {};

SwiperCore.use([Navigation, Pagination]);


export const PostCard = (props) => {
    const {measure, item, style, index, setModalState, scrollInfo, scrollHistory, parent, autoplay, isDetailPage} = props;
    const router = useRouter();

    const [scrapCount, setScrapCount] = useState(item?.scrap_count || 0);

    useEffect(() => {
        if (scrollInfo && style.height && typeof(style.height) !== "string") {
            scrollInfo.current.idxToHeight[index] = style.height;
        }
    }, [style]);

    const [maxLines, setMaxLines] = useState(expandInfo[item?.id] || isDetailPage  ? 5000 : 2);

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const shareLink = `${SITE_URL}/palette/${item?.id || ''}`;
    const shareModalRef = useRef(null);
    useOutsideAlerter(shareModalRef, () => setIsShareModalOpen(false));

    const [isEditPostModalOpen, setIsEditPostModalOpen] = useState(false);

    const onShareClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'PostCardShareClick', {
                postId: item?.id,
            }
        );
    };

    const onShareSubmit = (target) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'PostCardShareSubmit', {
                postId: item?.id,
                target,
            }
        );
    };

    useEffect(() => {
        measure();
    }, [item]);

    useEffect(() => {
        if (maxLines > 2) {
            expandInfo[item?.id] = true;
        }
    }, [maxLines]);

    const throttleRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            if (throttleRef.current) {
                return;
            }
            throttleRef.current = setTimeout(() => {
                console.log('measure!')
                throttleRef.current = null;
                clearTimeout(throttleRef.current);
                measure();
            }, 100);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(throttleRef.current);
        }
    }, []);

    const localOptionSwiperIndex = props.optionSwiperIndex || optionSwiperIndex;

    const moveToCreatorPage = (code) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'PostCardCreatorClick',
            {
                postId: item?.id,
            }
        );
        if (!code) return;
        for (const key in window.history.state) {
            if (key.toLowerCase().includes('modal')) {
                router.replace(getCreatorRoute(code));
                return
            }
        }
        router.push(getCreatorRoute(code));
    };

    const kakaoShareCallback = () => {
        let imageUrl;
        if (item.media_list.length === 0) {
            imageUrl = null;
        }

        const firstMedia = item.media_list[0];
        if (firstMedia.type === 'youtube') {
            const youtubeId = getYoutubeIdFromUrl(firstMedia.url);
            imageUrl = `https://img.youtube.com/vi/${youtubeId}/0.jpg`;
        } else if (firstMedia.type === 'image') {
            imageUrl = firstMedia.url;
        }

        if (!imageUrl) {
            imageUrl = 'https://d1cnx04b8cgzcv.cloudfront.net/static/hemeko_logo_v2.png';
        }

        try {
            Kakao.Share.sendDefault({
                objectType: 'feed',
                content: {
                    title: item.user.nickname,
                    description: item.text,
                    imageUrl: imageUrl,
                    link: {
                        mobileWebUrl: shareLink,
                    },
                },
                buttons: [
                    {
                        title: '컨텐츠 바로가기',
                        link: {
                            mobileWebUrl: shareLink,
                        },
                    },
                ]
            });
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    const onContentView = (media) => {
        typeof mixpanel !== 'undefined' && mixpanel.track('ViewPalettePost', {
            post_id: item.id,
            post_nickname: item.user.nickname,
            media_id: media.id,
            media_type: media.type,
        });
    }

    const user = useContext(UserContext).user;

    const onShowMoreClick = () => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'PostCardShowMoreClick'
        );
        openEditPostModal();
    };

    const {openModal, closeModal} = useContext(ModalContext);

    const openEditPostModal = () => {
        openModal(() => {
            return <EditPostModal
                post={item} close={closeModal}
                isOwner={item.user.nickname && item.user.nickname === user?.nickname}
            />;
        });
    };

    const onScrap = () => {
        setScrapCount(prev => prev + 1);
        item.scrap_count += 1;
    };

    const onUnscrap = () => {
        setScrapCount(prev => Math.max(prev - 1, 0));
        item.scrap_count -= 1;
    };

    return (
        item ?
            <>
                <div className={styles.cardRoot}>
                    <div className={styles.cardContainer}>
                        <div className={styles.headingContainer}>
                            {
                                item.user.profile_image_url
                                    ?
                                    <div onClick={() => moveToCreatorPage(item.user.code)} className={classNames(styles.profileImage)}>
                                        <Image unoptimized src={item.user.profile_image_url} width={32} height={32} />
                                    </div>
                                    : <DefaultProfileIcon onClick={() => moveToCreatorPage(item.user.code)} className={styles.profileImage} viewBox="0 0 32 32" />
                            }
                            <div className={styles.userInfoContainer}>
                                <div className={utilStyles.noLineHeight}>
                                    <Nickname value={`${item.user.nickname}`} creatorCode={item.user.code} className={styles.userNickname} />
                                    {
                                        item.user.is_verified &&
                                        <CreatorBadgeIcon className={classNames(utilStyles.verticalMiddle, styles.creatorBadge)} viewBox="0 0 16 16" />
                                    }
                                </div>
                            </div>
                            <div onClick={onShowMoreClick} style={{cursor: 'pointer'}}>
                                <ShowMoreIcon viewBox="0 0 24 24" width={24} height={24} />
                            </div>
                        </div>
                        <ContentSection
                            id={item.id}
                            mediaList={item.media_list}
                            mediaSize={item.media_size}
                            autoplay={autoplay}
                            onContentView={onContentView}
                        />
                        <div className={classNames(styles.contentContainer, item.media_list.length > 1 ? undefined : styles.contentContainerSingleItem)}>
                            <div className={styles.postButtonsContainer}>
                                <div className={styles.scrapButtonContainer}>
                                    <PostScrapButton
                                        postId={item.id}
                                        renderScrapped={() => <ScrapIconActive viewBox="0 0 24 24" className={utilStyles.cursorPointer} width={24} height={24} />}
                                        renderUnscrapped={() => <InactiveScrapIcon viewBox="0 0 24 24" className={styles.scrapIcon} />}
                                        onScrap={onScrap}
                                        onUnscrap={onUnscrap}
                                    />
                                    <div className={styles.scrapCount}>
                                        {scrapCount}
                                    </div>
                                </div>
                                <div className={styles.shareButton}>
                                    <ShareButton
                                        shareTitle={`${item.text.length > 10 ? item.text.substring(0, 10) + '...' : item.text}`}
                                        shareLink={shareLink}
                                        kakaoShareCallback={kakaoShareCallback}
                                        offsetX={0}
                                        tailOffsetX={2}
                                        onClick={onShareClick}
                                        onShare={onShareSubmit}
                                    >
                                        <ShareIcon
                                            viewBox="0 0 24 24" className={utilStyles.cursorPointer} width={24} height={24} />
                                    </ShareButton>
                                </div>
                                {/*<ReplyIcon viewBox="0 0 24 24" className={styles.replyIcon} onClick={() => openCommentModal(item)} />*/}
                            </div>
                            <div className={classNames(item.product_options.length === 1 && styles.singleOptionSwiperWrapper)}>
                                <OptionSwiper
                                    item={item} setModalState={setModalState}
                                    productOptions={item.product_options}
                                    postId={item.id} optionSwiperIndex={localOptionSwiperIndex}
                                />
                            </div>
                            <div className={styles.textContainer}>
                                <PostContent
                                    key={item.id}
                                    measure={measure}
                                    item={item} maxLines={maxLines} setMaxLines={setMaxLines}
                                />
                                {/*<span className={styles.seeMoreComments} onClick={() => openCommentModal(item)}>댓글 {item.comment_count}개 모두보기</span>*/}
                            </div>
                        </div>
                    </div>
                </div>
            </>
            :
            <div className={styles.cardRoot} style={{height: scrollHistory && scrollHistory.current.idxToHeight[index] ? scrollHistory.current.idxToHeight[index] : 700}}>
                <div className={classNames(styles.cardContainer, utilStyles.flexCol)} style={{height: '100%'}}>
                    <div className={styles.headingContainer}>
                        <div className={classNames(styles.profileImage)} style={{backgroundColor: '#DBDBDB'}}>
                        </div>
                        <div className={styles.userInfoContainer}>
                            <div className={utilStyles.noLineHeight}>
                                <div style={{width: 100, height: 15, backgroundColor: '#DBDBDB'}} />
                            </div>
                        </div>
                    </div>
                    <div style={{width: '100%', flex: 1, backgroundColor: '#DBDBDB'}} />
                    <div style={{padding: "0 24px 20px 24px"}}>
                        <div className={utilStyles.inlineBlock} style={{marginTop: 12}}>
                            <div style={{display: "inline-block", width: 24, marginRight: 16, height: 24, backgroundColor: '#DBDBDB', borderRadius: 9999}} />
                            <div style={{display: "inline-block", width: 24, height: 24, backgroundColor: '#DBDBDB', borderRadius: 9999}} />
                        </div>
                        <div>
                            <div className={classNames(utilStyles.flexRow)} style={{marginTop: 16}}>
                                <div style={{flex: 1, height: 76, backgroundColor: '#DBDBDB', borderRadius: 5}}></div>
                            </div>
                        </div>
                        <div style={{marginTop: 12}}>
                            <div style={{height: 18, width: '100%', backgroundColor: '#DBDBDB'}}></div>
                            <div style={{height: 18, width: '100%', backgroundColor: '#DBDBDB', marginTop: 8}}></div>
                            <div style={{height: 13, width: 100, backgroundColor: '#DBDBDB', marginTop: 6}}></div>
                        </div>
                    </div>
                </div>
            </div>
        // <div style={{height: scrollHistory && scrollHistory.current.idxToHeight[index] ? scrollHistory.current.idxToHeight[index] : 800}}>
        //     Loading
        // </div>
    )
};
